import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '../../../shared/store/auth/auth.state';
import { Observable, of } from 'rxjs';
import { HttpRequestService } from '../http-request/http-request.service';
import { ILoginRequest, IOtpResponse, IPatientLoginRequest } from '../../models/login-request.model';
import { HttpHeaders } from '@angular/common/http';
import { API } from '../../constants/api-url';
import { GroupById, KeycloakGroup } from '../../../core/modules/admin-modules/administrator/role-and-permission/models/keycloak-group.model';
import { PATIENT_PORTAL_ROUTE_PREFIX, SYSTEM_CONST } from '../../constants/system-constants.model';
import { IActionPermission } from '../../models/action-permission.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserType } from '../../constants/user-type-enum';
import * as forge from 'node-forge';
import { Router } from '@angular/router';
import { HelperService } from '../common/helper.service';
import { LOCAL_STORAGE } from '../../constants/localstorage-enum';
import { IKeycloakTokenResponse } from '../../models/keycloak-token-detail.model';
import { ILoggedInUser } from '../../models/logged-in-user.model';
import { ROUTE_DASHBOARD } from '../../constants/route-consts';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private store = inject(Store);
  private httpService = inject(HttpRequestService);
  private helperService = inject(HelperService);
  private router = inject(Router);
  private pageAccessCache = new Map<string, IActionPermission>();

  login(loginDetails: ILoginRequest): Observable<IKeycloakTokenResponse> {
    return this.httpService.post<IKeycloakTokenResponse, null>(API.AUTHENTICATION.LOGIN, {
      username: this.encryptData(loginDetails.email),
      password: this.encryptData(loginDetails.password)
    }, null, null, this.helperService.getSkipBearerTokenContext());
  }

  patientLogin(loginDetails: IPatientLoginRequest): Observable<IKeycloakTokenResponse> {
    return this.httpService.post<IKeycloakTokenResponse, null>(API.AUTHENTICATION.PATIENT_LOGIN, {
      memberId: loginDetails.memberId,
      otpCode: loginDetails.otpCode
    }, null, null, this.helperService.getSkipBearerTokenContext());
  }

  resetPassword(payload: Record<string, string>, validationKey: string): Observable<boolean> {
    payload['value'] = this.encryptData(payload['value']);
    return this.httpService.put<boolean, null>(`${API.AUTHENTICATION.RESET_PASSWORD}/${validationKey}`, payload);
  }

  changePassword(payload: Record<string, string | boolean>): Observable<boolean> {
    payload['value'] = this.encryptData(payload['value'] as string);
    return this.httpService.put<boolean, null>(API.COMMON.RESET_PASSWORD, payload);
  }

  forgotPassword(username: string): Observable<boolean> {
    const redirectionUrl = window.location.origin + this.router.url.replace('login', 'reset-password');
    const encryptedUsername = encodeURIComponent(this.encryptData(username));
    return this.httpService.get<boolean, null>(`${API.AUTHENTICATION.FORGOT_PASS_SEND_MAIL}?userName=${encryptedUsername}&redirectionUrl=${redirectionUrl}`, null, null, this.helperService.getSkipBearerTokenContext());
  }

  getUserByUsername(username: string): Observable<ILoggedInUser> {
    return this.httpService.get<ILoggedInUser, null>(`${API.COMMON.USER}/username/${username}`, null, this.createAuthorizationHeader());
  }

  isLoggedIn(): boolean {
    return this.store.selectSnapshot(AuthState.isLoggedIn);
  }

  isSuperAdmin(): boolean {
    const currentUserRole = this.store.selectSnapshot(AuthState.role);
    return this.checkIsSuperAdminRole(currentUserRole);
  }

  isAdmin(): boolean {
    const currentUserRole = this.store.selectSnapshot(AuthState.role);
    return this.checkIsAdminRole(currentUserRole);
  }

  isMultipleProvider(): boolean {
    const userType = this.store.selectSnapshot(AuthState.userType);
    const providerIds = this.store.selectSnapshot(AuthState.providerIds);
    if (userType == UserType.Payer || (userType == UserType.TPA && (!providerIds || providerIds.length > 1)))
      return true;
    return false;
  }

  isProviderCallFromStore(): boolean {
    const userType = this.store.selectSnapshot(AuthState.userType);
    const providerIds = this.store.selectSnapshot(AuthState.providerIds);
    if (userType == UserType.Payer || (userType == UserType.TPA && !providerIds))
      return false;
    return true;
  }

  isMultiplePayer(): boolean {
    const userType = this.store.selectSnapshot(AuthState.userType);
    const payerIds = this.store.selectSnapshot(AuthState.payerIds);
    if (userType == UserType.Provider || (userType == UserType.TPA && (!payerIds || payerIds.length > 1)))
      return true;
    return false;
  }

  isPayerCallFromStore(): boolean {
    const userType = this.store.selectSnapshot(AuthState.userType);
    const payerIds = this.store.selectSnapshot(AuthState.payerIds);
    if (userType == UserType.Provider || (userType == UserType.TPA && !payerIds))
      return false;
    return true;
  }

  isTpaWithMultiplePayerAndProviderAccess(): boolean {
    const userType = this.store.selectSnapshot(AuthState.userType);
    if (userType !== UserType.TPA) return false;
    const payerIds = this.store.selectSnapshot(AuthState.payerIds);
    const providerIds = this.store.selectSnapshot(AuthState.providerIds);
    const multiplePayer = !payerIds || payerIds.length > 1;
    const multipleProvider = !providerIds || providerIds.length > 1;
    return multiplePayer || multipleProvider;
  }

  checkIsSuperAdminRole(group: KeycloakGroup | GroupById): boolean {
    const value = group?.attributes?.[SYSTEM_CONST.KEYCLOAK_ATTRIBUTES.IS_SUPER_ADMIN]?.[0];
    return value === '1' || value === 'true' || value === true;
  }

  checkIsAdminRole(group: KeycloakGroup | GroupById): boolean {
    const value = group?.attributes?.[SYSTEM_CONST.KEYCLOAK_ATTRIBUTES.IS_ADMIN]?.[0];
    return value === '1' || value === 'true' || value === true;
  }

  currentUserPermissions(): string[] {
    return this.store.selectSnapshot(AuthState.permissions);
  }

  currentUserRole(): KeycloakGroup {
    return this.store.selectSnapshot(AuthState.role);
  }

  currentUserType(): number | undefined {
    return this.store.selectSnapshot(AuthState.userType);
  }

  isPayerUserType(): boolean {
    return this.currentUserType() === UserType.Payer;
  }

  isProviderUserType(): boolean {
    return this.currentUserType() === UserType.Provider;
  }

  isTPAUserType(): boolean {
    return this.currentUserType() === UserType.TPA;
  }

  isPatientUserType(): boolean {
    return this.currentUserType() === UserType.Patient;
  }

  getToken(): string | undefined {
    return this.store.selectSnapshot(AuthState.token);
  }

  getTokenExpiryTime(): number | undefined {
    return this.store.selectSnapshot(AuthState.tokenExpiryAt);
  }

  getRefreshToken(): string | undefined {
    return this.store.selectSnapshot(AuthState.refreshToken);
  }

  getRefreshTokenExpiryTime(): number | undefined {
    return this.store.selectSnapshot(AuthState.refreshTokenExpiryAt);
  }

  sendPatientOTP(memberId: string): Observable<IOtpResponse> {
    return this.httpService.post<IOtpResponse, null>(`${API.AUTHENTICATION.PATIENT_SEND_OTP}?memberId=${memberId}`, null);
  }

  logout(sessionTimeout: boolean = false): Observable<boolean> {
    const refreshToken = this.getRefreshToken();
    if (refreshToken)
      return this.httpService.post(API.AUTHENTICATION.LOGOUT, { refreshToken: refreshToken, sessionTimeout: sessionTimeout }, null, null, this.helperService.getSkipLoaderContext());
    else
      return of(true);
  }

  refreshToken(): Observable<IKeycloakTokenResponse> {
    const refreshToken = this.getRefreshToken();
    if (refreshToken)
      return this.httpService.post(API.AUTHENTICATION.REFRESH_TOKEN, { refreshToken: refreshToken }, null, null, this.helperService.getSkipBearerTokenContext());
    else
      return of(null);
  }

  getPageAccess(permission: string): IActionPermission {
    if (this.pageAccessCache.has(permission))
      return this.pageAccessCache.get(permission);

    const userPermissions = this.currentUserPermissions();
    const specificPermissions = userPermissions?.filter((p) => p.split('.')[0].toLowerCase() == permission.toLowerCase());
    if (specificPermissions) {
      const result: IActionPermission = {
        screen: specificPermissions.includes(`${permission}.read.screen`),
        list: specificPermissions.includes(`${permission}.read.list`),
        get: specificPermissions.includes(`${permission}.read.get`),
        create: specificPermissions.includes(`${permission}.write.create`),
        update: specificPermissions.includes(`${permission}.write.update`),
        delete: specificPermissions.includes(`${permission}.write.delete`),
        adjudication: specificPermissions.includes(`${permission}.write.adjudication`),
        assignment: specificPermissions.includes(`${permission}.write.assignment`),
        communication: specificPermissions.includes(`${permission}.write.communication`),
        download: specificPermissions.includes(`${permission}.write.download`),
        email: specificPermissions.includes(`${permission}.write.email`),
        activate: specificPermissions.includes(`${permission}.write.activate`),
        convert: specificPermissions.includes(`${permission}.write.convert`),
        pbmCreate: specificPermissions.includes(`${permission}.write.pbm-create`),
        pbmCancel: specificPermissions.includes(`${permission}.write.pbm-cancel`),
        pbmInquiry: specificPermissions.includes(`${permission}.read.pbm-inquiry`),
        pbmOverride: specificPermissions.includes(`${permission}.write.pbm-override`),
        erpList: specificPermissions.includes(`${permission}.write.erp-list`),
        erpUpdate: specificPermissions.includes(`${permission}.write.erp-update`),
        settling: specificPermissions.includes(`${permission}.write.settling`),
        settled: specificPermissions.includes(`${permission}.write.settled`),
        highCostAdjudication: specificPermissions.includes(`${permission}.write.highcost-adjudication`)
      };
      this.pageAccessCache.set(permission, result);
      return result;
    }
    return {};
  }

  clearPageAccessCache(): void {
    this.pageAccessCache.clear();
  }

  hasAccess(permission: string): boolean {
    const userPermissions = this.currentUserPermissions() || [];
    return userPermissions?.includes(permission.toLowerCase());
  }

  decodeToken(token: string) {
    const helper = new JwtHelperService();
    const decodedToken = helper.decodeToken(token);
    return decodedToken;
  }

  getLoginPagePath(isPatientUser: boolean = false) {
    return isPatientUser ? '/patient-login' : '/login';
  }

  getDashboardPath(isPatientUser: boolean = false) {
    return isPatientUser ? `${PATIENT_PORTAL_ROUTE_PREFIX}dashboard` : ROUTE_DASHBOARD;
  }

  redirectToDashboard(isPatientUser: boolean = false) {
    this.router.navigate([this.getDashboardPath(isPatientUser)]);
  }

  redirectToLoginPage(isPatientUser: boolean = false) {
    this.router.navigate([this.getLoginPagePath(isPatientUser)]);
  }

  encryptData(data: string): string {
    const publicKey = forge.pki.publicKeyFromPem(environment.publicKey);
    const encrypted = publicKey.encrypt(forge.util.encodeUtf8(data), 'RSA-OAEP', { md: forge.md.sha256.create() });
    return forge.util.encode64(encrypted);
  }

  removeItemsFromLocalStoage(isLogout?: boolean) {
    if (isLogout) {
      localStorage.removeItem(LOCAL_STORAGE.AUTH_DETAILS);
      localStorage.removeItem(LOCAL_STORAGE.CURRENT_USER);
      localStorage.removeItem(LOCAL_STORAGE.LAST_URL);
      localStorage.removeItem(LOCAL_STORAGE.MASTER_ACTIVE_TAB);
    }
    localStorage.removeItem(LOCAL_STORAGE.CLAIM_UPLOADS_HISTORY);
    localStorage.removeItem(LOCAL_STORAGE.GRID_CONFIGS);
    localStorage.removeItem(LOCAL_STORAGE.PRE_AUTH_ADVANCE_SEARCH);
    localStorage.removeItem(LOCAL_STORAGE.ADVANCE_PRE_AUTH_SEARCH);
    localStorage.removeItem(LOCAL_STORAGE.SEARCH_CLAIM);
    localStorage.removeItem(LOCAL_STORAGE.CLAIM_UPLOADS);
    localStorage.removeItem(LOCAL_STORAGE.PREPARE_CLAIM);
    localStorage.removeItem(LOCAL_STORAGE.CONVERT_CLAIM);
    localStorage.removeItem(LOCAL_STORAGE.ELIGIBILITY_TRANSACTION);
    localStorage.removeItem(LOCAL_STORAGE.DENIAL_MANAGEMENT);
    localStorage.removeItem(LOCAL_STORAGE.PRESCRIPTION_TRANSACTION_LIST);
    localStorage.removeItem(LOCAL_STORAGE.PREAUTH_TRANSACTION_ACTIVE_TAB);
    localStorage.removeItem(LOCAL_STORAGE.ADVANCE_PREAUTH_TRANSACTION_ACTIVE_TAB);
    localStorage.removeItem(LOCAL_STORAGE.CLAIM_TRANSACTION_ACTIVE_TAB);
    localStorage.removeItem(LOCAL_STORAGE.PRESCRIPTION_TRANSACTION_ACTIVE_TAB);
    localStorage.removeItem(LOCAL_STORAGE.BENEFICIARY_LIST);
    localStorage.removeItem(LOCAL_STORAGE.PATIENT_POLICY_LIST);
    localStorage.removeItem(LOCAL_STORAGE.PATIENT_CLAIM_LIST);
    localStorage.removeItem(LOCAL_STORAGE.PATIENT_PREAUTH_LIST);
    localStorage.removeItem(LOCAL_STORAGE.PATIENT_PRESCRIPTION_LIST);
    localStorage.removeItem(LOCAL_STORAGE.CLAIM_REIMBURSEMENT);
    localStorage.removeItem(LOCAL_STORAGE.PATIENT_CLAIM_REIMBURSEMENT_LIST);
    localStorage.removeItem(LOCAL_STORAGE.CLAIM_BATCH_LIST_FILTERS);
    localStorage.removeItem(LOCAL_STORAGE.PROVIDER_BATCH_LIST_FILTERS);
    localStorage.removeItem(LOCAL_STORAGE.REPORTS_ACTIVE_TAB);
  }

  isRefreshTokenExpired(): boolean {
    const helper = new JwtHelperService();
    const authDetails = JSON.parse(localStorage.getItem(LOCAL_STORAGE.AUTH_DETAILS) as string) as IKeycloakTokenResponse;
    return helper.isTokenExpired(authDetails?.refresh_token);
  };

  private createAuthorizationHeader(): HttpHeaders {
    return new HttpHeaders({ 'Authorization': `Bearer ${this.getToken()}` });
  }
}